import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { TEAM } from "@/components/AboutContent";
import { useLang } from "@/i18n/LanguageContext";

/**
 * Homepage teaser · Le collectif Les Arteurs.
 * Version SOBRE alignée avec /qui-sommes-nous — médaillons initiales,
 * pas de photos, titre + rôle, lien CTA vers la page complète.
 * Cohérent avec le PDF V2 validé par le directeur du projet UNESCO.
 */

export default function TeamCarousel() {
  const { lang } = useLang();
  const en = lang === "en";
  return (
    <section className="mt-24 md:mt-32 py-20 md:py-28 bg-fg/[0.03] border-y border-fg/10" data-testid="team-carousel">
      <div className="px-6 md:px-12 max-w-[1400px] mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12 md:mb-16">
          <div className="max-w-2xl">
            <div className="meta-label text-cinabre">§ {en ? "Who we are" : "Qui sommes-nous"}</div>
            <h2 className="mt-4 font-serif font-light text-5xl md:text-7xl leading-[0.95] tracking-tight">
              {en ? <>Seven <em className="italic">hands</em>.<br/>One thread.</> : <>Sept <em className="italic">mains</em>.<br/>Un fil.</>}
            </h2>
            <p className="mt-6 text-fg/70 leading-relaxed text-lg">
              {en ? <>Founders, active members, volunteers — seven journeys that weave{" "}<em className="italic">Le Fil du Midi</em>.</> : <>Fondateurs, membres actifs, bénévoles — sept parcours qui composent{" "}<em className="italic">Le Fil du Midi</em>.</>}
            </p>
          </div>

          <a
            href="/qui-sommes-nous"
            className="group inline-flex items-center gap-3 font-mono text-[11px] tracking-[0.24em] uppercase text-cinabre hover:text-fg transition-colors"
            data-testid="team-cta-full"
          >
            <span>{en ? "Read the full journeys" : "Les parcours complets"}</span>
            <ArrowRight size={16} strokeWidth={1.4} className="transition-transform group-hover:translate-x-1" />
          </a>
        </div>

        {/* Grille médaillons sobres */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 md:gap-5">
          {TEAM.map((m, i) => (
            <motion.a
              key={m.name}
              href="/qui-sommes-nous"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className="group flex flex-col items-center text-center border border-fg/12 bg-bg p-5 md:p-6 hover:border-cinabre/50 transition-colors duration-500"
              data-testid={`home-team-${i}`}
            >
              {m.photo ? (
                <div
                  className={`w-16 h-16 md:w-20 md:h-20 rounded-full overflow-hidden transition-transform duration-500 group-hover:scale-105 ${m.accent ? "ring-2 ring-cinabre ring-offset-2 ring-offset-bg" : "border border-fg/15"}`}
                  aria-hidden="true"
                >
                  <img
                    src={m.photo}
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover"
                    style={m.photoPos ? { objectPosition: m.photoPos } : undefined}
                  />
                </div>
              ) : (
                <div
                  className={`w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center font-serif text-xl md:text-2xl leading-none tracking-tight transition-transform duration-500 group-hover:scale-105 ${
                    m.accent
                      ? "bg-cinabre text-bg"
                      : "bg-fg/[0.06] text-fg/75 border border-fg/15"
                  }`}
                  aria-hidden="true"
                >
                  {m.initials}
                </div>
              )}
              <h3 className="mt-4 font-serif font-light text-lg md:text-xl leading-tight tracking-tight">
                {m.name.split(" ").slice(0, 2).join(" ")}
                {m.name.split(" ").length > 2 && <><br/><span className="text-fg/75">{m.name.split(" ").slice(2).join(" ")}</span></>}
              </h3>
              <div className="mt-2 font-mono text-[9px] tracking-[0.18em] uppercase leading-tight text-fg/55">
                {(en && m.role_en ? m.role_en : m.role).split(" · ")[0]}
              </div>
            </motion.a>
          ))}
        </div>

        <p className="mt-10 md:mt-12 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/50 text-center md:text-left">
          {en ? "Click a medallion to read the detailed journeys" : "Cliquez sur un médaillon pour lire les parcours détaillés"}
        </p>
      </div>
    </section>
  );
}
